/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { canPlaySound } from '../lib/soundThrottle'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  List,
  MapPin,
  MessageCircle,
  MoreVertical,
  Search,
} from 'lucide-react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { API_BASE_URL, TOKEN_KEY, adminApi, unwrap } from '../services/api'
import type { DashboardOrderParams, HolidayEntry, OrdersSummary } from '../services/api'
import OrdersMapView from '../components/orders/OrdersMapView'
import type { OrdersMapOrder } from '../services/api'
import OrderDetailPanel from '../components/orders/OrderDetailPanel'
import OrdersTable from '../components/orders/OrdersTable'
import OrderFilters from '../components/orders/OrderFilters'
import OrderAssignmentModal from '../components/orders/OrderAssignmentModal'
import OrderTimeline from '../components/orders/OrderTimeline'
import CreateOrderModal from '../components/orders/CreateOrderModal'
import type { OrderDetail } from '../components/orders/OrderDetailPanel'
import { openPrintLabels } from '../components/orders/PrintLabel'
import { openServiceLabelsMulti, type ServiceGroup, type PrintOrderMeta } from '../components/orders/printDocs'
import OrderQRCode from '../components/orders/OrderQRCode'
import type { QrPayload } from '../components/orders/orderQr'
import ReorderModal from '../components/orders/ReorderModal'
import LoadErrorState from '../components/common/LoadErrorState'
import {
  SERVER_ERROR_MESSAGE,
  SERVER_TIMEOUT_MESSAGE,
  withServerTimeout,
} from '../components/common/loadErrorUtils'
import EmptyState from '../components/shared/EmptyState'
import ErrorState from '../components/shared/ErrorState'
import { useColors } from '../hooks/useColors'
import { useToast } from '../contexts/ToastContext'
import { useSocket } from '../hooks/useSocket'
import { formatServiceName } from '../utils/serviceMetadata'

type StatusFilter =
  | 'all'
  | 'placed'
  | 'confirmed'
  | 'searching_agent'
  | 'agent_assigned'
  | 'en_route_pickup'
  | 'arrived_pickup'
  | 'picked_up'
  | 'at_facility'
  | 'in_wash'
  | 'in_iron'
  | 'quality_check'
  | 'ready'
  | 'out_for_delivery'
  | 'arrived_dropoff'
  | 'delivered'
  | 'cancelled'
  | 'refunded'
  | 'no_show'
  | 'returned'
  | 'failed'
  | 'reschedule_pending'
  // composite preset used by the "Needs assignment" option
  | 'placed,confirmed,searching_agent,agent_assigned'

interface Order {
  id: string
  order_number: string
  customer_name?: string | null
  customer_phone?: string | null
  address_summary?: string | null
  service_name?: string | null
  agent_id?: string | null
  agent_name?: string | null
  agent_is_online?: boolean | null
  partner_name?: string | null
  status: string
  total: number
  subtotal?: number | null
  discount?: number | null
  final_amount?: number | null
  /**
   * The order's value - the single figure the table must show. Resolved by the
   * backend (utils/order-amount): a manual final-amount adjustment wins over the
   * calculated amount. Never recompute this in the frontend, or the table starts
   * disagreeing with the summary row and the dashboard.
   */
  final_order_amount?: number | null
  /** The same order before any manual adjustment, for the "was ₹X" hint. */
  calculated_amount?: number | null
  amount_collected?: number | null
  manual_adjustment_amount?: number | null
  coupon_code?: string | null
  pickup_slot?: string | null
  payment_status?: string | null
  payment_mode?: string | null
  delivery_model?: string | null
  promised_delivery_at?: string | null
  workload_score?: number | null
  workload_minutes?: number | null
  express_slot_key?: string | null
  assignment_urgency?: string | null
  delivery_promise_label?: string | null
  qr_token?: string | null
  qr_payload?: QrPayload | null
  group_count?: number
  items_ready?: number
  items_total?: number
  groups?: { code?: string | null; label: string; status: string; delivery_model?: string }[]
}

interface AgentOption {
  id: string
  name?: string | null
}

interface WhatsAppLink {
  order_number: string
  link: string
}

interface OrdersPayload {
  orders?: Order[]
  total?: number
  page?: number
  limit?: number
}

type ViewMode = 'list' | 'calendar' | 'map'

interface CalendarOrder {
  id: string
  order_number?: string
  customer_name?: string | null
  status?: string
  total?: number
  payment_status?: string | null
  payment_collected_method?: string | null
  pickup_date?: string | null
  /** TRUE when the order contributed nothing to the day's revenue. */
  cancelled?: boolean
}

/**
 * One day of the SALES calendar. The question it answers is "how much business
 * did Rapidry receive on this day", so the primary pair is keyed on when the
 * order was PLACED. Pickup, delivery and payment dates are separate business
 * dimensions and each has its own field - none of them may move an order's
 * revenue to another day.
 */
interface CalendarEntry {
  /** PRIMARY. Orders whose placement timestamp falls on this IST day. */
  orders_placed?: number
  /** PRIMARY. Final value of exactly those orders. Not collections. */
  order_revenue?: number
  /** Placed this day, later cancelled. Excluded from the two figures above. */
  cancelled_orders?: number
  /** OPERATIONAL. Active bags the fleet collects on this day. */
  pickups: number
  /** OPERATIONAL. Value of those pickups - workload, not sales. */
  pickup_value?: number
  deliveries: number
  /** Pickups scheduled for this day that were cancelled or failed. */
  cancelled_pickups?: number
  /** The orders behind `orders_placed`. */
  orders: CalendarOrder[]
  /** The orders scheduled for pickup here, for the operations view. */
  pickup_orders?: CalendarOrder[]
  /** @deprecated pickup-day value that used to masquerade as the day's revenue. */
  total_revenue?: number
  /** @deprecated read `orders_placed`. */
  placed_orders?: number
  /** @deprecated read `order_revenue`. */
  placed_value?: number
}

type CalendarPayload = Record<string, CalendarEntry>

const TABLE_SHADOW = '0px 2px 8px rgba(0,0,0,0.04)'

const toLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const getStatusStyles = (status: string) => {
  const normalized = status.toLowerCase()

  // §9 - placed / confirmed → neutral (accent gold)
  if (normalized === 'placed' || normalized === 'confirmed') {
    return {
      background: 'rgba(214,185,123,0.12)',
      color: 'var(--accent-text)',
      border: '1px solid var(--accent)',
    }
  }

  // §9 - searching_agent / agent_unassigned → amber (warning)
  if (normalized === 'searching_agent' || normalized === 'agent_unassigned') {
    return {
      background: 'var(--color-warning-bg)',
      color: 'var(--color-warning)',
      border: '1px solid var(--color-warning-border)',
    }
  }

  // §9 - assigned / agent_assigned → blue (info)
  if (normalized === 'agent_assigned') {
    return {
      background: 'var(--color-info-bg)',
      color: 'var(--color-info)',
      border: '1px solid var(--color-info-border)',
    }
  }

  if (normalized === 'picked_up' || normalized === 'out_for_delivery') {
    return {
      background: 'var(--color-warning-bg)',
      color: 'var(--color-warning)',
      border: '1px solid var(--color-warning-border)',
    }
  }

  if (normalized === 'at_facility' || normalized === 'in_wash' || normalized === 'in_iron' || normalized === 'quality_check' || normalized === 'ready') {
    return {
      background: 'var(--color-purple-bg)',
      color: 'var(--color-purple)',
      border: '1px solid var(--color-purple-border)',
    }
  }

  if (normalized === 'delivered') {
    return {
      background: 'var(--color-success-bg)',
      color: 'var(--color-success)',
      border: '1px solid var(--color-success-border)',
    }
  }

  // Reschedule pending → amber/warning
  if (normalized === 'reschedule_pickup_pending' || normalized === 'reschedule_delivery_pending') {
    return {
      background: 'var(--color-warning-bg)',
      color: 'var(--color-warning)',
      border: '1px solid var(--color-warning-border)',
    }
  }

  // §9 - cancelled + fallback → red (error)
  return {
    background: 'var(--color-error-bg)',
    color: 'var(--color-error)',
    border: '1px solid var(--color-error-border)',
  }
}


const getInitials = (name: string | null | undefined) => {
  if (!name) {
    return 'NA'
  }

  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

const formatAmount = (value: number) => {
  // Show paise only when fractional (decimal-weight pricing → ₹346.50), else
  // keep whole-rupee display. Rounded to 2dp first to avoid float artefacts.
  const rounded = Math.round(Number(value || 0) * 100) / 100
  const hasPaise = Math.round(rounded * 100) % 100 !== 0
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  }).format(rounded)
}

// The order's value, straight from the backend's resolved field. The fallback
// chain only covers older payloads (a socket push, a cached row) that predate
// final_order_amount - it is NOT a second pricing implementation.
const orderValueOf = (order: {
  final_order_amount?: number | null
  final_amount_paid?: number | null
  calculated_amount?: number | null
  total?: number | null
}) => {
  if (order.final_order_amount != null) return Number(order.final_order_amount)
  if (order.final_amount_paid != null) return Number(order.final_amount_paid)
  if (order.calculated_amount != null) return Number(order.calculated_amount)
  return Number(order.total || 0)
}

// Operational totals are reconciled against cash, so they are shown to the
// paisa - never abbreviated to "₹18.4K".
const formatExactAmount = (value: number) => {
  const rounded = Math.round(Number(value || 0) * 100) / 100
  const hasPaise = Math.round(rounded * 100) % 100 !== 0
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(rounded)
}

const DATE_RANGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'all', label: 'All dates' },
  { value: 'custom', label: 'Custom range' },
]

const dateRangeLabel = (value: string) =>
  DATE_RANGE_OPTIONS.find((option) => option.value === value)?.label ?? value

const maskPhone = (phone?: string | null) => {
  if (!phone) return ''
  const clean = phone.replace(/\D/g, '')
  if (clean.length < 10) return phone
  return `${clean.slice(0, 2)}XXXXX${clean.slice(-3)}`
}

/**
 * The three numbers a calendar day leads with, resolved once.
 *
 * The cell and the day drawer must never disagree, so neither derives its own:
 * both read this. The deprecated aliases are honoured as a fallback so a stale
 * or cached API response still renders the right figure instead of a zero.
 *
 * `cancelled` is reported separately and is NOT inside `orders`/`revenue` -
 * cancelled work contributes to neither, the same rule the dashboard and the
 * reports use.
 */
export const calendarDaySummary = (entry?: CalendarEntry | null) => ({
  orders: Number(entry?.orders_placed ?? entry?.placed_orders ?? 0),
  revenue: Number(entry?.order_revenue ?? entry?.placed_value ?? 0),
  cancelled: Number(entry?.cancelled_orders ?? 0),
})

const formatMonthParam = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

const getMonthLabel = (date: Date) =>
  date.toLocaleString('en-IN', { month: 'long', year: 'numeric' })

const toDateKey = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const buildCalendarDays = (monthDate: Date) => {
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
  const startOffset = (firstDay.getDay() + 6) % 7
  const startDate = new Date(firstDay)
  startDate.setDate(firstDay.getDate() - startOffset)

  const days: Array<{ date: Date; inMonth: boolean }> = []
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i)
    days.push({
      date,
      inMonth: date.getMonth() === monthDate.getMonth(),
    })
  }
  return days
}

export default function OrdersPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const panelAssignTriggerRef = useRef<HTMLDivElement | null>(null)
  const colors = useColors()
  const { addToast } = useToast()

  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [calendarMonth, setCalendarMonth] = useState(new Date())
  const [calendarData, setCalendarData] = useState<CalendarPayload>({})
  const [calendarLoading, setCalendarLoading] = useState(false)
  const [calendarError, setCalendarError] = useState('')
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null)
  const [holidayMap, setHolidayMap] = useState<Record<string, HolidayEntry>>({})
  const [holidayError, setHolidayError] = useState('')
  const [mapOrders, setMapOrders] = useState<OrdersMapOrder[]>([])
  const [mapLoading, setMapLoading] = useState(false)
  const [mapError, setMapError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('placed,confirmed,searching_agent,agent_assigned')
  const [dateFilter, setDateFilter] = useState('last_7_days')
  const [zoneFilter, setZoneFilter] = useState('all_zones')
  const [zoneOptions, setZoneOptions] = useState<string[]>([])
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all_methods')
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null)
  const [assignDropdown, setAssignDropdown] = useState<{
    orderId: string
    anchorEl: HTMLElement
  } | null>(null)
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set())
  const [bulkStatus, setBulkStatus] = useState('')
  const [bulkAgentId, setBulkAgentId] = useState('')
  // In-flight guards so a double-click can't fire duplicate bulk/assign requests.
  const [bulkBusy, setBulkBusy] = useState(false)
  const [assigningOrderId, setAssigningOrderId] = useState<string | null>(null)
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [totalOrders, setTotalOrders] = useState(0)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [showReorderModal, setShowReorderModal] = useState(false)
  const [reorderTargetId, setReorderTargetId] = useState<string | null>(null)
  const [whatsappLinks, setWhatsappLinks] = useState<WhatsAppLink[]>([])
  const [showWhatsappModal, setShowWhatsappModal] = useState(false)
  const [reschedulePendingCount, setReschedulePendingCount] = useState(0)
  // Pending-COD is its own filter, not a status: COD orders whose payment is
  // still pending. The backend owns the definition (utils/order-amount).
  const [pendingCodOnly, setPendingCodOnly] = useState(false)
  // Delivered bags whose COD was never marked collected - the reconciliation
  // set the dashboard tile links to. A stricter pending-COD, so the two are
  // mutually exclusive rather than additive.
  const [deliveredUnmarkedCodOnly, setDeliveredUnmarkedCodOnly] = useState(false)
  const [customDateFrom, setCustomDateFrom] = useState('')
  const [customDateTo, setCustomDateTo] = useState('')
  const [summary, setSummary] = useState<OrdersSummary | null>(null)
  const [summaryError, setSummaryError] = useState('')
  const selectAllRef = useRef<HTMLInputElement | null>(null)

  // ── Read filters from URL on mount (§3.1.3) ────────────────────────
  useEffect(() => {
    const status = searchParams.get('status')
    const zone = searchParams.get('zone')
    const date = searchParams.get('date')
    const pm = searchParams.get('payment_method')
    if (status && status !== 'all') setStatusFilter(status as StatusFilter)
    if (zone && zone !== 'all_zones') setZoneFilter(zone)
    if (date && date !== 'last_7_days') setDateFilter(date)
    if (pm && pm !== 'all_methods') setPaymentMethodFilter(pm)
    if (searchParams.get('pending_cod') === '1') setPendingCodOnly(true)
    if (searchParams.get('delivered_unmarked_cod') === '1') setDeliveredUnmarkedCodOnly(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Persist filters in URL query params (§3.1.3) ──────────────────
  useEffect(() => {
    const params = new URLSearchParams()
    if (statusFilter !== 'all') params.set('status', statusFilter)
    if (zoneFilter !== 'all_zones') params.set('zone', zoneFilter)
    if (dateFilter !== 'last_7_days') params.set('date', dateFilter)
    if (paymentMethodFilter !== 'all_methods') params.set('payment_method', paymentMethodFilter)
    if (pendingCodOnly) params.set('pending_cod', '1')
    if (deliveredUnmarkedCodOnly) params.set('delivered_unmarked_cod', '1')
    const qs = params.toString()
    const target = qs ? `?${qs}` : location.pathname
    navigate(target, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, zoneFilter, dateFilter, paymentMethodFilter, pendingCodOnly, deliveredUnmarkedCodOnly])

  // Populate the zone filter from the real configured delivery zones so it stays
  // in sync with the Delivery Zones page (instead of a hardcoded list).
  useEffect(() => {
    let cancelled = false
    const loadZones = async () => {
      try {
        const response = await adminApi.getDeliveryZones()
        const payload = unwrap<{ zones?: Array<{ name?: string }> }>(response)
        const names = (payload?.zones || [])
          .map((z) => (z?.name || '').trim())
          .filter((name): name is string => name.length > 0)
        if (!cancelled) setZoneOptions(Array.from(new Set(names)))
      } catch {
        if (!cancelled) setZoneOptions([])
      }
    }
    void loadZones()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const handleCreateOrder = () => setShowCreateModal(true)
    document.addEventListener('create-order', handleCreateOrder)
    return () => document.removeEventListener('create-order', handleCreateOrder)
  }, [])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim())
      setPage(1)
    }, 300)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [searchTerm])

  // ONE place that turns the on-screen filter controls into query params, shared
  // by the list, the summary aggregate and the CSV export - so all three always
  // describe the same set of orders. The date/zone/payment-method controls used
  // to be wired to state that was never sent, so they silently did nothing.
  const filterParams = useMemo(() => {
    const params: DashboardOrderParams = {
      status:
        statusFilter === 'all'
          ? undefined
          : statusFilter === 'reschedule_pending'
            ? 'reschedule_pickup_pending,reschedule_delivery_pending'
            : statusFilter,
      search: debouncedSearch || undefined,
      zone: zoneFilter === 'all_zones' ? undefined : zoneFilter,
      payment_method: paymentMethodFilter === 'all_methods' ? undefined : paymentMethodFilter,
    }
    if (pendingCodOnly) params.pending_cod = 1
    if (deliveredUnmarkedCodOnly) params.delivered_unmarked_cod = 1
    if (dateFilter === 'custom') {
      if (customDateFrom) params.date_from = customDateFrom
      if (customDateTo) params.date_to = customDateTo
      params.date_range = 'custom'
    } else {
      params.date_range = dateFilter
    }
    return params
  }, [
    statusFilter,
    debouncedSearch,
    zoneFilter,
    paymentMethodFilter,
    pendingCodOnly,
    deliveredUnmarkedCodOnly,
    dateFilter,
    customDateFrom,
    customDateTo,
  ])

  // Monotonic request ids: filters can change twice in quick succession (the
  // URL-restore effect fires right after mount), and without this an earlier,
  // slower response could land last and repaint rows that no longer match the
  // active filters - the header totals would then disagree with the table.
  const ordersRequestId = useRef(0)
  const summaryRequestId = useRef(0)

  const loadOrders = useCallback(async () => {
    const requestId = ordersRequestId.current + 1
    ordersRequestId.current = requestId
    setLoading(true)
    setError('')

    try {
      const response = await withServerTimeout(
        adminApi.getOrders({
          ...filterParams,
          page,
          limit,
          order: 'created_at:desc',
        }),
      )
      if (ordersRequestId.current !== requestId) return

      const payload = unwrap<OrdersPayload>(response)
      const nextOrders = Array.isArray(payload?.orders) ? payload.orders : []
      setOrders(nextOrders)
      setTotalOrders(Number(payload?.total || 0))
      setReschedulePendingCount(
        nextOrders.filter(
          (order) =>
            order.status === 'reschedule_pickup_pending' || order.status === 'reschedule_delivery_pending',
        ).length,
      )
    } catch (loadError) {
      if (ordersRequestId.current !== requestId) return
      setError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      if (ordersRequestId.current === requestId) setLoading(false)
    }
  }, [filterParams, limit, page])

  // Aggregated in Postgres. Deliberately NOT derived from `orders` - that array
  // is one page, so summing it would understate every total and get slower as
  // Rapidry grows.
  const loadSummary = useCallback(async () => {
    const requestId = summaryRequestId.current + 1
    summaryRequestId.current = requestId
    setSummaryError('')
    try {
      const response = await withServerTimeout(adminApi.getOrdersSummary(filterParams))
      if (summaryRequestId.current !== requestId) return
      const payload = unwrap<OrdersSummary>(response)
      setSummary(payload || null)
    } catch {
      if (summaryRequestId.current !== requestId) return
      setSummaryError('Totals unavailable')
    }
  }, [filterParams])

  const loadCalendar = useCallback(async () => {
    setCalendarLoading(true)
    setCalendarError('')

    try {
      const response = await withServerTimeout(
        adminApi.getOrdersCalendar({ month: formatMonthParam(calendarMonth) }),
      )
      const payload = unwrap<CalendarPayload>(response)
      setCalendarData(payload || {})
    } catch (loadError) {
      setCalendarError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setCalendarLoading(false)
    }
  }, [calendarMonth])

  const loadHolidays = useCallback(async () => {
    setHolidayError('')

    try {
      const response = await withServerTimeout(adminApi.getHolidays(calendarMonth.getFullYear()))
      const payload = unwrap<HolidayEntry[]>(response)
      const nextMap = (payload || []).reduce<Record<string, HolidayEntry>>((acc, holiday) => {
        if (holiday?.date) {
          acc[holiday.date] = holiday
        }
        return acc
      }, {})
      setHolidayMap(nextMap)
    } catch (loadError) {
      setHolidayError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    }
  }, [calendarMonth])

  const loadMapOrders = useCallback(async () => {
    setMapLoading(true)
    setMapError('')

    try {
      const response = await withServerTimeout(adminApi.getOrdersMapData())
      const payload = unwrap<OrdersMapOrder[]>(response)
      setMapOrders(Array.isArray(payload) ? payload : [])
    } catch (loadError) {
      setMapError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setMapLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadOrders()
  }, [loadOrders])

  useEffect(() => {
    void loadSummary()
  }, [loadSummary])

  // ── Realtime updates ────────────────────────────────────────────────
  // Backend emits these events on every order mutation. We react in-place
  // (no full reload) so scroll position, filters, and selection survive.
  //
  // Sound mute persists per browser-session (§3.2.4 - sessionStorage).
  // Topbar also reads this same key.
  const isSoundMuted = () => {
    try {
      return sessionStorage.getItem('rapidry_admin_sound_muted') === '1'
    } catch {
      return false
    }
  }

  // ── Sound throttle (§3.2.3 - one play per 2 s) ─────────────────────
  // Uses the shared module-level gate so this page and the global
  // NotificationProvider never double-play the same event.
  const throttledPlay = useCallback((file: string, volume = 1) => {
    if (isSoundMuted()) return
    if (!canPlaySound()) return
    const audio = new Audio(file)
    audio.volume = Math.min(Math.max(volume, 0), 1)
    audio.play().catch(() => { /* autoplay blocked. Ignore */ })
  }, [])

  // cash-chime.mp3 / cancel-alert.mp3 were never shipped (404) - use the one
  // bundled sound for all cues.
  const playOrderSound = useCallback(() => throttledPlay('/notification.mp3', 0.6), [throttledPlay])
  const playCashChime = useCallback(() => throttledPlay('/notification.mp3'), [throttledPlay])
  const playCancelAlert = useCallback(() => throttledPlay('/notification.mp3'), [throttledPlay])

  const handleOrderCreated = useCallback(
    (payload: any) => {
      if (!payload?.order_id) return

      const newOrder: Order = {
        id: payload.order_id,
        order_number: payload.order_number,
        customer_name: payload.customer_name ?? null,
        customer_phone: payload.customer_phone ?? null,
        address_summary: payload.address_summary ?? null,
        service_name: payload.service_name ?? null,
        agent_name: null,
        partner_name: null,
        status: payload.status || 'placed',
        total: Number(payload.total) || 0,
        pickup_slot: payload.pickup_slot ?? null,
        payment_status: payload.payment_status ?? null,
        payment_mode: payload.payment_method ?? null,
        delivery_model: payload.delivery_model ?? null,
        promised_delivery_at: payload.promised_delivery_at ?? null,
        workload_score: payload.workload_score ?? null,
        workload_minutes: payload.workload_minutes ?? null,
        express_slot_key: payload.express_slot_key ?? null,
        assignment_urgency: payload.assignment_urgency ?? null,
        delivery_promise_label: payload.delivery_promise_label ?? null,
      }

      setOrders((prev) => {
        if (prev.some((o) => o.id === newOrder.id)) return prev
        return [newOrder, ...prev]
      })
      setTotalOrders((n) => n + 1)

      playOrderSound()

      // Toast with "Open" CTA → opens OrderDetailDrawer (§3.2 / §9.D.6)
      const orderId = payload.order_id
      addToast(
        `🛎️ New order ${payload.order_number || ''} from ${payload.customer_name || 'a customer'}`,
        'success',
        {
          label: 'Open',
          onClick: () => { void handleViewOrder(orderId) },
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addToast, playOrderSound]
  )

  const handleStatusChanged = useCallback((payload: any) => {
    if (!payload?.order_id || !payload?.status) return
    setOrders((prev) =>
      prev.map((o) => (o.id === payload.order_id ? { ...o, status: payload.status } : o))
    )
    // Re-render open drawer if it matches (§3.3.3)
    setSelectedOrder((prev) =>
      prev && prev.id === payload.order_id
        ? { ...prev, status: payload.status }
        : prev
    )
  }, [])

  const handleAgentAssigned = useCallback((payload: any) => {
    if (!payload?.order_id) return
    setOrders((prev) =>
      prev.map((o) =>
        o.id === payload.order_id
          ? {
              ...o,
              agent_name: payload.agent_name || o.agent_name,
              status: payload.status || (o.status === 'placed' ? 'agent_assigned' : o.status),
            }
          : o
      )
    )
    // Re-render open drawer if it matches (§3.3.3)
    setSelectedOrder((prev) =>
      prev && prev.id === payload.order_id
        ? {
            ...prev,
            agent_name: payload.agent_name || prev.agent_name,
            agent_id: payload.agent_id || prev.agent_id,
            status: payload.status || (prev.status === 'placed' ? 'agent_assigned' : prev.status),
          }
        : prev
    )
  }, [])

  const handlePaymentUpdated = useCallback((payload: any) => {
    if (!payload?.order_id) return
    setOrders((prev) =>
      prev.map((o) =>
        o.id === payload.order_id
          ? {
              ...o,
              payment_status: payload.payment_status || o.payment_status,
              payment_mode: payload.payment_method || o.payment_mode,
              // The emit carries the order's final amount, so an adjustment made
              // in another tab lands on this row immediately.
              final_order_amount: payload.amount != null ? Number(payload.amount) : o.final_order_amount,
            }
          : o
      )
    )
    // Pending-COD and the value totals both move when a payment changes; they
    // are backend aggregates, so re-ask rather than guess.
    void loadSummary()
  }, [loadSummary])

  const handleCashCollected = useCallback(
    (payload: any) => {
      if (!payload?.order_id) return
      setOrders((prev) =>
        prev.map((o) =>
          o.id === payload.order_id
            ? { ...o, payment_status: 'paid', payment_mode: o.payment_mode || 'cod' }
            : o
        )
      )
      playCashChime()
      addToast(`💰 Cash collected on ${payload.order_number || payload.order_id.slice(0, 6)}`, 'success')
    },
    [addToast, playCashChime]
  )

  const handleOrderCancelled = useCallback(
    (payload: any) => {
      if (!payload?.order_id) return
      setOrders((prev) =>
        prev.map((o) => (o.id === payload.order_id ? { ...o, status: 'cancelled' } : o))
      )
      playCancelAlert()
      addToast(
        `⚠️ Order ${payload.order_number || payload.order_id.slice(0, 6)} cancelled`,
        'error'
      )
    },
    [addToast, playCancelAlert]
  )

  const handleOrderRefunded = useCallback(
    (payload: any) => {
      if (!payload?.order_id) return
      setOrders((prev) =>
        prev.map((o) => (o.id === payload.order_id ? { ...o, status: 'refunded' } : o))
      )
      addToast(
        `↩️ Order ${payload.order_number || payload.order_id.slice(0, 6)} refunded`,
        'info'
      )
    },
    [addToast]
  )

  const handleAgentUnassigned = useCallback((payload: any) => {
    if (!payload?.order_id) return
    setOrders((prev) =>
      prev.map((o) =>
        o.id === payload.order_id
          ? {
              ...o,
              agent_id: null,
              agent_name: null,
              agent_is_online: null,
              status: payload.status || 'searching_agent',
            }
          : o
      )
    )
    setSelectedOrder((prev) =>
      prev && prev.id === payload.order_id
        ? { ...prev, agent_id: null, agent_name: null, agent_is_online: null, status: payload.status || 'searching_agent' }
        : prev
    )
    addToast('Agent rejected the task. Reassign this order from Orders.', 'info')
  }, [addToast])

  const handleRescheduleRequested = useCallback(
    (payload: any) => {
      if (!payload?.order_id) return
      setReschedulePendingCount((n) => n + 1)
      setOrders((prev) => {
        const exists = prev.some((o) => o.id === payload.order_id)
        if (exists) {
          return prev.map((o) =>
            o.id === payload.order_id
              ? { ...o, status: payload.status || 'reschedule_pickup_pending' }
              : o
          )
        }
        const incoming: Order = {
          id: payload.order_id,
          order_number: payload.order_number || payload.order_id.slice(0, 8).toUpperCase(),
          customer_name: payload.customer_name ?? null,
          customer_phone: null,
          address_summary: null,
          service_name: null,
          agent_id: null,
          agent_name: null,
          agent_is_online: null,
          partner_name: null,
          status:
            payload.status ||
            (payload.type === 'delivery' ? 'reschedule_delivery_pending' : 'reschedule_pickup_pending'),
          total: Number(payload.total || 0),
          pickup_slot: payload.new_slot || null,
          payment_status: null,
          payment_mode: null,
        }
        return [incoming, ...prev]
      })
      playOrderSound()
      addToast(
        `📅 Reschedule requested for ${payload.order_number || payload.order_id.slice(0, 6)}`,
        'info'
      )
    },
    [addToast, playOrderSound]
  )

  useSocket({
    'order:created': handleOrderCreated,
    'order:status_changed': handleStatusChanged,
    'order:agent_assigned': handleAgentAssigned,
    'order:agent_unassigned': handleAgentUnassigned,
    'order:payment_updated': handlePaymentUpdated,
    'order:cash_collected': handleCashCollected,
    'order:cancelled': handleOrderCancelled,
    'order:refunded': handleOrderRefunded,
    'order:reschedule_requested': handleRescheduleRequested,
  })

  useEffect(() => {
    if (viewMode === 'calendar') {
      void loadCalendar()
    }
  }, [loadCalendar, viewMode])

  useEffect(() => {
    if (viewMode === 'calendar') {
      void loadHolidays()
    }
  }, [loadHolidays, viewMode])

  useEffect(() => {
    if (viewMode === 'map') {
      void loadMapOrders()
    }
  }, [loadMapOrders, viewMode])

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await adminApi.getAgents()
        const payload = unwrap<{ data?: AgentOption[] } | AgentOption[]>(response)
        if (Array.isArray(payload)) {
          setAgents(payload)
          return
        }
        setAgents(Array.isArray(payload?.data) ? payload.data : [])
      } catch (error) {
        console.error(error)
      }
    }

    void loadAgents()
  }, [])

  useEffect(() => {
    const currentIds = new Set(orders.map((order) => order.id))
    setSelectedOrders((prev) => {
      const next = new Set(Array.from(prev).filter((id) => currentIds.has(id)))
      return next
    })
  }, [orders])

  useEffect(() => {
    if (!selectAllRef.current) {
      return
    }

    const total = orders.length
    const selectedCount = selectedOrders.size
    selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < total
  }, [orders.length, selectedOrders])

  const hasFilters =
    statusFilter !== 'all' ||
    debouncedSearch.length > 0 ||
    dateFilter !== 'last_7_days' ||
    zoneFilter !== 'all_zones'

  const startCount = totalOrders === 0 ? 0 : (page - 1) * limit + 1
  const endCount = Math.min(page * limit, totalOrders)
  const canGoPrev = page > 1
  const canGoNext = page * limit < totalOrders

  const activeFilterPills = useMemo(() => {
    const pills: Array<{ key: string; label: string; onClear: () => void }> = []

    if (statusFilter !== 'all') {
      pills.push({
        key: 'status',
        label: `Status: ${toLabel(statusFilter)}`,
        onClear: () => setStatusFilter('all'),
      })
    }

    if (debouncedSearch) {
      pills.push({
        key: 'search',
        label: `Search: ${debouncedSearch}`,
        onClear: () => setSearchTerm(''),
      })
    }

    if (zoneFilter !== 'all_zones') {
      pills.push({
        key: 'zone',
        label: `Zone: ${zoneFilter}`,
        onClear: () => setZoneFilter('all_zones'),
      })
    }

    if (dateFilter !== 'last_7_days') {
      pills.push({
        key: 'date',
        label: `Date: ${dateFilter}`,
        onClear: () => setDateFilter('last_7_days'),
      })
    }

    if (paymentMethodFilter !== 'all_methods') {
      pills.push({
        key: 'payment_method',
        label: `Payment: ${paymentMethodFilter}`,
        onClear: () => setPaymentMethodFilter('all_methods'),
      })
    }

    return pills
  }, [statusFilter, debouncedSearch, zoneFilter, dateFilter, paymentMethodFilter])

  const calendarDays = useMemo(() => buildCalendarDays(calendarMonth), [calendarMonth])
  const calendarMonthLabel = useMemo(() => getMonthLabel(calendarMonth), [calendarMonth])
  const selectedCalendarEntry = selectedCalendarDate ? calendarData[selectedCalendarDate] : null
  const selectedCalendarOrders = selectedCalendarEntry?.orders || []
  // Read the day's own totals rather than re-summing the list: the list
  // deliberately includes cancelled orders (ops still need to see them) while
  // the totals exclude them, and re-deriving here would reintroduce the
  // disagreement between the cell and the drawer.
  const {
    orders: selectedCalendarPlaced,
    revenue: selectedCalendarRevenue,
    cancelled: selectedCalendarCancelled,
  } = calendarDaySummary(selectedCalendarEntry)

  useEffect(() => {
    if (viewMode !== 'calendar') {
      return
    }

    const monthKey = formatMonthParam(calendarMonth)
    const today = new Date()
    const todayKey = toDateKey(today)
    const defaultDate = todayKey.startsWith(monthKey)
      ? todayKey
      : toDateKey(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1))

    setSelectedCalendarDate((prev) => (prev && prev.startsWith(monthKey) ? prev : defaultDate))
  }, [calendarMonth, viewMode])

  /**
   * Open the detail panel IMMEDIATELY from the row we already have.
   *
   * This used to `await adminApi.getOrderDetail(id)` before rendering anything,
   * so every "View" click froze the page for the full round trip - and then the
   * panel fetched the very same endpoint again on mount, paying it twice. The
   * row already carries the customer, status, slot, payment and amount, so the
   * panel can paint at once and fill in items/proofs/activity as they arrive
   * (see OrderDetailPanel's own detail fetch).
   */
  const openOrderPanel = (order: Order) => {
    setSelectedOrder(order as unknown as OrderDetail)
  }

  /**
   * Open by id only - for callers with no row in hand (the "Open" action on a
   * new-order toast, the map view). Uses the row from the current page when it
   * is there, and only falls back to a fetch when it is not.
   */
  const handleViewOrder = async (id: string) => {
    const known = orders.find((order) => order.id === id)
    if (known) {
      openOrderPanel(known)
      return
    }
    const response = await adminApi.getOrderDetail(id)
    const detail = unwrap<OrderDetail>(response)
    setSelectedOrder(detail)
  }

  const handleAssignAgent = async (orderId: string, agentId: string) => {
    if (assigningOrderId) return
    setAssigningOrderId(orderId)
    try {
      const assignResponse = await adminApi.assignAgent(orderId, agentId)
      setAssignDropdown(null)

      const response = await adminApi.getOrders({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: debouncedSearch || undefined,
        page,
        limit,
      })
      const payload = unwrap<OrdersPayload>(response)
      setOrders(Array.isArray(payload?.orders) ? payload.orders : [])
      setTotalOrders(Number(payload?.total || 0))

      if (selectedOrder?.id === orderId) {
        const detailResponse = await adminApi.getOrderDetail(orderId)
        const detail = unwrap<OrderDetail>(detailResponse)
        setSelectedOrder(detail)
      }
      if (assignResponse?.data?._warning) {
        addToast(assignResponse.data._warning, 'info')
      } else {
        addToast('Agent assigned successfully', 'success')
      }
    } catch (err: any) {
      if (err?.response?.status === 422 && err?.response?.data?.code === 'AGENT_NOT_APPROVED') {
        addToast("This agent isn't approved yet. Approve their KYC first.", 'error')
      } else if (err?.response?.status === 422 && err?.response?.data?.code === 'AGENT_OFFLINE_ASSIGNMENT_DISABLED') {
        addToast('This agent is offline. Choose an online agent or enable offline pre-assignment.', 'error')
      } else {
        addToast(err?.response?.data?.message || 'Failed to assign agent', 'error')
      }
    } finally {
      setAssigningOrderId(null)
    }
  }

  const handleToggleSelect = (orderId: string, checked: boolean) => {
    setSelectedOrders((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(orderId)
      } else {
        next.delete(orderId)
      }
      return next
    })
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(new Set(orders.map((order) => order.id)))
    } else {
      setSelectedOrders(new Set())
    }
  }

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus) {
      addToast('Select a status to apply.', 'error')
      return
    }

    const confirmed = window.confirm(`Change status for ${selectedOrders.size} orders?`)
    if (!confirmed) {
      return
    }

    if (bulkBusy) return
    setBulkBusy(true)
    const count = selectedOrders.size
    try {
      await adminApi.bulkUpdateOrderStatus(Array.from(selectedOrders), bulkStatus)
      setSelectedOrders(new Set())
      setBulkStatus('')
      await loadOrders()
      addToast(`Updated ${count} orders`, 'success')
    } finally {
      setBulkBusy(false)
    }
  }

  const handleBulkAssignAgent = async () => {
    if (!bulkAgentId) {
      addToast('Select an agent to assign.', 'error')
      return
    }

    const confirmed = window.confirm(`Assign agent to ${selectedOrders.size} orders?`)
    if (!confirmed) {
      return
    }

    if (bulkBusy) return
    setBulkBusy(true)
    const count = selectedOrders.size
    try {
      const response = await adminApi.bulkAssignAgent(Array.from(selectedOrders), bulkAgentId)
      const payload = unwrap<{ whatsapp_links?: WhatsAppLink[] }>(response)
      const links = Array.isArray(payload?.whatsapp_links) ? payload.whatsapp_links : []
      if (links.length > 0) {
        setWhatsappLinks(links)
        setShowWhatsappModal(true)
      }
      setSelectedOrders(new Set())
      setBulkAgentId('')
      await loadOrders()
      addToast(`Assigned agent to ${count} orders`, 'success')
    } finally {
      setBulkBusy(false)
    }
  }

  const handleBulkPrintLabels = async () => {
    try {
      const selectedIds = Array.from(selectedOrders)
      const detailResponses = await Promise.all(
        selectedIds.map(async (id) => {
          const [detailRes, labelsRes] = await Promise.all([
            adminApi.getOrderDetail(id),
            adminApi.getOrderGroupLabels(id).catch(() => null),
          ])
          const detail = unwrap<OrderDetail>(detailRes)
          const labelsPayload = labelsRes ? (unwrap(labelsRes) as { labels?: ServiceGroup[] }) : null
          return { detail, groups: Array.isArray(labelsPayload?.labels) ? labelsPayload.labels : [] }
        }),
      )

      // Prefer one service-bag label per group (group-centric). Fall back to the
      // legacy per-item labels only for orders that have no groups.
      const groupEntries = detailResponses
        .filter((r) => r.groups.length > 0)
        .map((r) => ({
          meta: {
            orderNumber: r.detail.order_number,
            displayId: r.detail.display_id,
            customerName: r.detail.customer?.name || r.detail.customer_name || 'Customer',
            createdAt: r.detail.created_at || null,
          } as PrintOrderMeta,
          groups: r.groups,
        }))

      if (groupEntries.length > 0) {
        openServiceLabelsMulti(groupEntries)
      }

      const legacy = detailResponses.filter((r) => r.groups.length === 0)
      if (legacy.length > 0) {
        await openPrintLabels(legacy.map(({ detail }) => ({
          orderNumber: detail.order_number,
          customerName: detail.customer?.name || detail.customer_name || 'Customer',
          // Distinct service name(s) in the order, shown at the top of the label.
          serviceNames: Array.from(
            new Set(
              (detail.items || [])
                .map((item) => formatServiceName(item.service_name || ''))
                .filter(Boolean),
            ),
          ),
          // Garment breakdown uses the item name (Shirt, Bedsheet…), not the service.
          items: (detail.items || []).map((item) => ({
            name: item.name || formatServiceName(item.service_name || '') || 'Item',
            quantity: Number(item.quantity || 1),
          })),
          createdAt: detail.created_at || null,
          qrPayload: detail.qr_payload,
          qrToken: detail.qr_token,
          orderId: detail.id,
        })), addToast)
      }
    } catch (error) {
      console.error(error)
      addToast('Unable to open print labels. Please try again.', 'error')
    }
  }

  const handleQuickWhatsApp = async (order: Order) => {
    try {
      const response = await adminApi.getWhatsAppLink(order.id, {
        custom_message: `Hi ${order.customer_name || 'there'}! This is Rapidry Admin.`,
      })
      const payload = unwrap<{ url?: string }>(response)
      if (payload?.url) {
        window.open(payload.url, '_blank')
        return
      }
    } catch (error) {
      console.error(error)
    }

    if (order.customer_phone) {
      const cleanPhone = order.customer_phone.replace(/[\s\-\+\(\)]/g, '')
      const phoneWithCountry = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`
      const fallbackMessage = encodeURIComponent(`Hi ${order.customer_name || 'there'}! This is Rapidry Admin.`)
      window.open(`https://wa.me/${phoneWithCountry}?text=${fallbackMessage}`, '_blank')
    } else {
      addToast('Customer phone is not available for WhatsApp.', 'error')
    }
  }

  const handleOpenInvoice = (orderId: string) => {
    // The invoice opens in a new tab, which can't carry the Authorization
    // header - the backend allows the JWT via ?token= for GET downloads, so
    // append it (otherwise the tab shows "Unauthorized: token missing").
    const token = localStorage.getItem(TOKEN_KEY)
    const url = `${API_BASE_URL}/admin/orders/${orderId}/invoice${
      token ? `?token=${encodeURIComponent(token)}` : ''
    }`
    window.open(url, '_blank')
  }

  const handleOpenReorder = (orderId: string) => {
    setReorderTargetId(orderId)
    setShowReorderModal(true)
  }

  const clearAllFilters = () => {
    setStatusFilter('all')
    setSearchTerm('')
    setDateFilter('last_7_days')
    setZoneFilter('all_zones')
    setPaymentMethodFilter('all_methods')
    setPendingCodOnly(false)
    setCustomDateFrom('')
    setCustomDateTo('')
    setPage(1)
  }

  const handleExportCsv = async () => {
    setExportLoading(true)

    try {
      // Exactly what is on screen - same resolver as the list and the summary.
      const params = filterParams

      const response = await adminApi.exportOrdersCsv(params)
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = `rapidry-orders-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error(error)
      addToast('Unable to export CSV. Please try again.', 'error')
    } finally {
      setExportLoading(false)
    }
  }

  // Compact operational strip: four numbers a shift lead reads in two seconds.
  // Every figure is aggregated by the backend over the SAME filters as the table.
  const summaryTiles = summary
    ? [
        {
          key: 'orders',
          label: dateFilter === 'today' ? "Today's orders" : `Orders · ${dateRangeLabel(dateFilter)}`,
          value: String(summary.orders_count),
          hint: summary.excluded_orders_count > 0
            ? `${summary.excluded_orders_count} cancelled/failed excluded from value`
            : null,
          emphasis: false,
        },
        {
          key: 'value',
          label: dateFilter === 'today' ? 'Daily order value' : 'Order value',
          // Exact to the paisa - this gets reconciled, not skimmed.
          value: formatExactAmount(summary.order_value),
          hint: Math.round(summary.calculated_value * 100) !== Math.round(summary.order_value * 100)
            ? `calculated ${formatExactAmount(summary.calculated_value)} before adjustments`
            : 'value of orders placed (final amounts)',
          emphasis: true,
        },
        {
          key: 'collected',
          label: 'Collected',
          value: formatExactAmount(summary.amount_collected),
          hint: 'money actually received',
          emphasis: false,
        },
        {
          key: 'cod',
          label: 'Pending COD',
          value: `${summary.pending_cod.count} ${summary.pending_cod.count === 1 ? 'order' : 'orders'}`,
          hint: `${formatExactAmount(summary.pending_cod.amount)} pending${
            summary.pending_cod_all_time.count > summary.pending_cod.count
              ? ` · ${summary.pending_cod_all_time.count} all dates (${formatExactAmount(summary.pending_cod_all_time.amount)})`
              : ''
          }`,
          emphasis: summary.pending_cod.count > 0,
        },
      ]
    : []

  return (
    <div>
      <h1 className="sr-only">Orders</h1>

      {viewMode === 'list' ? (
        <div
          aria-label="Orders summary"
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '16px',
            padding: '14px 18px',
            marginBottom: '12px',
            boxShadow: TABLE_SHADOW,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '14px',
          }}
        >
          {summaryError ? (
            <div className="font-body" style={{ fontSize: '13px', color: 'var(--color-error)' }}>
              {summaryError}{' '}
              <button
                type="button"
                onClick={() => { void loadSummary() }}
                style={{ border: 'none', background: 'transparent', color: colors.accentText, cursor: 'pointer', fontWeight: 600, padding: 0 }}
              >
                retry
              </button>
            </div>
          ) : !summary ? (
            [0, 1, 2, 3].map((i) => (
              <div key={`sk-${i}`} style={{ display: 'grid', gap: '6px' }}>
                <div style={{ height: '11px', width: '70%', borderRadius: 4, background: colors.surface }} />
                <div style={{ height: '20px', width: '50%', borderRadius: 4, background: colors.surface }} />
              </div>
            ))
          ) : (
            summaryTiles.map((tile) => (
              <div
                key={tile.key}
                style={{
                  display: 'grid',
                  gap: '2px',
                  borderLeft: tile.emphasis ? `3px solid ${colors.accent}` : '3px solid transparent',
                  paddingLeft: '10px',
                }}
              >
                <span
                  className="font-body"
                  style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: colors.textMuted }}
                >
                  {tile.label}
                </span>
                <span
                  className="font-display"
                  style={{ fontSize: '20px', fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}
                >
                  {tile.value}
                </span>
                {tile.hint ? (
                  <span className="font-body" style={{ fontSize: '11px', color: colors.textMuted }}>
                    {tile.hint}
                  </span>
                ) : null}
              </div>
            ))
          )}
        </div>
      ) : null}

      <div
        ref={panelAssignTriggerRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: '-1000px',
          left: '-1000px',
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: 'none'
        }}
      />

      <section style={{ padding: '24px 32px 0' }}>
        <div
          style={{
            background: colors.bgCard,
            borderRadius: '16px',
            padding: '16px 20px',
            boxShadow: TABLE_SHADOW,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ position: 'relative', width: '280px' }}>
            <Search
              size={16}
              color={colors.textMuted}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by order ID, customer..."
              aria-label="Search orders"
              className="font-body"
              style={{
                width: '100%',
                height: '40px',
                paddingLeft: '38px',
                border: `1.5px solid ${colors.border}`,
                borderRadius: '10px',
                fontSize: '14px',
                color: colors.textPrimary,
                background: colors.bgPrimary,
                outline: 'none',
              }}
              onFocus={(event) => {
                event.currentTarget.style.borderColor = colors.accent
                event.currentTarget.style.boxShadow = '0 0 0 3px rgba(214,185,123,0.1)'
              }}
              onBlur={(event) => {
                event.currentTarget.style.borderColor = colors.border
                event.currentTarget.style.boxShadow = 'none'
              }}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as StatusFilter)
              setPage(1)
            }}
            aria-label="Filter by order status"
            className="font-body"
            style={{
              width: '140px',
              height: '40px',
              border: `1.5px solid ${colors.border}`,
              borderRadius: '10px',
              fontSize: '14px',
              color: colors.textPrimary,
              background: colors.bgPrimary,
              padding: '0 10px',
            }}
          >
            <option value="all">All Status</option>
            <option value="placed,confirmed,searching_agent,agent_assigned">Needs assignment</option>
            <option value="placed">Placed</option>
            <option value="confirmed">Confirmed</option>
            <option value="searching_agent">Searching Agent</option>
            <option value="agent_assigned">Assigned</option>
            <option value="en_route_pickup">En route (pickup)</option>
            <option value="arrived_pickup">Arrived (pickup)</option>
            <option value="picked_up">Picked Up</option>
            <option value="at_facility">At Facility</option>
            <option value="in_wash">In Wash</option>
            <option value="in_iron">In Iron</option>
            <option value="quality_check">Quality Check</option>
            <option value="ready">Ready</option>
            <option value="out_for_delivery">Out for Delivery</option>
            <option value="arrived_dropoff">Arrived (dropoff)</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
            <option value="refunded">Refunded</option>
            <option value="no_show">No Show</option>
            <option value="returned">Returned</option>
            <option value="failed">Failed</option>
            <option value="reschedule_pending">Reschedule Pending{reschedulePendingCount > 0 ? ` (${reschedulePendingCount})` : ''}</option>
          </select>

          <button
            type="button"
            aria-label="Filter reschedule pending orders"
            onClick={() => {
              setStatusFilter(statusFilter === 'reschedule_pending' ? 'all' : 'reschedule_pending')
              setPage(1)
            }}
            className="font-body"
            style={{
              height: '32px',
              borderRadius: '999px',
              border: `1px solid ${statusFilter === 'reschedule_pending' ? colors.accent : colors.border}`,
              background: statusFilter === 'reschedule_pending' ? 'rgba(214,185,123,0.12)' : colors.bgPrimary,
              color: statusFilter === 'reschedule_pending' ? colors.accentText : colors.textPrimary,
              padding: '0 12px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            Reschedule pending
            {reschedulePendingCount > 0 ? (
              <span
                style={{
                  minWidth: '20px',
                  height: '20px',
                  borderRadius: '999px',
                  background: 'var(--color-warning)',
                  color: '#fff',
                  fontSize: '11px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 6px',
                }}
              >
                {reschedulePendingCount}
              </span>
            ) : null}
          </button>

          {/* "Which COD orders are still unpaid?" in one click. The count comes
              from the backend aggregate, which uses the same predicate as this
              filter, so the badge can never disagree with the rows. */}
          <button
            type="button"
            aria-pressed={pendingCodOnly}
            aria-label="Filter unpaid COD orders"
            title={
              summary
                ? `${summary.pending_cod.count} unpaid COD · ${formatExactAmount(summary.pending_cod.amount)} pending`
                : 'COD orders with payment still pending'
            }
            onClick={() => {
              setPendingCodOnly((prev) => !prev)
              setPage(1)
            }}
            className="font-body"
            style={{
              height: '32px',
              borderRadius: '999px',
              border: `1px solid ${pendingCodOnly ? colors.accent : colors.border}`,
              background: pendingCodOnly ? 'rgba(214,185,123,0.12)' : colors.bgPrimary,
              color: pendingCodOnly ? colors.accentText : colors.textPrimary,
              padding: '0 12px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            Pending COD
            {summary && summary.pending_cod.count > 0 ? (
              <span
                style={{
                  minWidth: '20px',
                  height: '20px',
                  borderRadius: '999px',
                  background: 'var(--color-error)',
                  color: '#fff',
                  fontSize: '11px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 6px',
                }}
              >
                {summary.pending_cod.count}
              </span>
            ) : null}
          </button>

          {/* Real date-range control. This was a decorative button wired to
              nothing, so the page always showed every order regardless of the
              filter state it was persisting into the URL. */}
          <label
            className="font-body"
            style={{
              height: '40px',
              border: `1.5px solid ${colors.border}`,
              borderRadius: '10px',
              background: colors.bgPrimary,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '0 10px',
              fontSize: '14px',
              color: colors.textPrimary,
            }}
          >
            <Calendar size={14} color={colors.textMuted} />
            {/* Named, not implied. This filter has always run on the order's
                PLACEMENT date; leaving it as a bare date range invited it to be
                read as pickup or payment, which are different questions with
                different answers. */}
            <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted, whiteSpace: 'nowrap' }}>
              Order date
            </span>
            <select
              value={dateFilter}
              onChange={(event) => {
                setDateFilter(event.target.value)
                setPage(1)
              }}
              aria-label="Filter by order placement date"
              className="font-body"
              style={{
                border: 'none',
                background: 'transparent',
                color: colors.textPrimary,
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {DATE_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          {dateFilter === 'custom' ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="date"
                value={customDateFrom}
                max={customDateTo || undefined}
                onChange={(event) => { setCustomDateFrom(event.target.value); setPage(1) }}
                aria-label="From date"
                className="font-body"
                style={{ height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', background: colors.bgPrimary, color: colors.textPrimary, padding: '0 8px' }}
              />
              <span className="font-body" style={{ color: colors.textMuted, fontSize: '13px' }}>to</span>
              <input
                type="date"
                value={customDateTo}
                min={customDateFrom || undefined}
                onChange={(event) => { setCustomDateTo(event.target.value); setPage(1) }}
                aria-label="To date"
                className="font-body"
                style={{ height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', background: colors.bgPrimary, color: colors.textPrimary, padding: '0 8px' }}
              />
            </div>
          ) : null}

          <select
            value={zoneFilter}
            onChange={(event) => setZoneFilter(event.target.value)}
            aria-label="Filter by zone"
            className="font-body"
            style={{
              width: '140px',
              height: '40px',
              border: `1.5px solid ${colors.border}`,
              borderRadius: '10px',
              fontSize: '14px',
              color: colors.textPrimary,
              background: colors.bgPrimary,
              padding: '0 10px',
            }}
          >
            <option value="all_zones">All Zones</option>
            {zoneOptions.map((zone) => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </select>

          <div
            style={{
              display: 'inline-flex',
              border: `1.5px solid ${colors.border}`,
              borderRadius: '999px',
              overflow: 'hidden',
              background: colors.bgPrimary,
            }}
          >
            <button
              type="button"
              className="font-body"
              onClick={() => setViewMode('list')}
              style={{
                height: '36px',
                padding: '0 14px',
                border: 'none',
                background: viewMode === 'list' ? colors.bgSidebar : 'transparent',
                color: viewMode === 'list' ? colors.bgPrimary : colors.textPrimary,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <List size={14} />
              List
            </button>
            <button
              type="button"
              className="font-body"
              onClick={() => setViewMode('calendar')}
              style={{
                height: '36px',
                padding: '0 14px',
                border: 'none',
                background: viewMode === 'calendar' ? colors.bgSidebar : 'transparent',
                color: viewMode === 'calendar' ? colors.bgPrimary : colors.textPrimary,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Calendar size={14} />
              Calendar
            </button>
            <button
              type="button"
              className="font-body"
              onClick={() => setViewMode('map')}
              style={{
                height: '36px',
                padding: '0 14px',
                border: 'none',
                background: viewMode === 'map' ? colors.bgSidebar : 'transparent',
                color: viewMode === 'map' ? colors.bgPrimary : colors.textPrimary,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <MapPin size={14} />
              Map
            </button>
          </div>

          <div style={{ flex: 1 }} />

          <button
            type="button"
            className="font-body"
            onClick={() => {
              void handleExportCsv()
            }}
            style={{
              height: '40px',
              padding: '0 16px',
              border: `1.5px solid ${colors.accent}`,
              borderRadius: '10px',
              background: 'transparent',
              color: colors.accentText,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: exportLoading ? 0.6 : 1,
            }}
            disabled={exportLoading}
            onMouseEnter={(event) => {
              event.currentTarget.style.background = 'rgba(214,185,123,0.08)'
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.background = 'transparent'
            }}
          >
            <Download size={16} />
            {exportLoading ? 'Exporting...' : 'Export CSV'}
          </button>

          <button
            type="button"
            className="font-body"
            onClick={() => setShowCreateModal(true)}
            style={{
              height: '40px',
              padding: '0 20px',
              border: 'none',
              borderRadius: '10px',
              background: colors.bgSidebar,
              color: colors.surface,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            + New Order
          </button>
        </div>
      </section>

      <OrderFilters hasFilters={hasFilters} pills={activeFilterPills} onClearAll={clearAllFilters} />

      {viewMode === 'calendar' ? (
        <section style={{ margin: '16px 32px' }}>
          <div
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              boxShadow: TABLE_SHADOW,
              padding: '16px 20px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <button
              type="button"
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              style={{
                height: '32px',
                padding: '0 10px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                background: colors.bgPrimary,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <ChevronLeft size={14} />
              Prev
            </button>

            <div className="font-display" style={{ fontSize: '18px', color: colors.textPrimary }}>
              {calendarMonthLabel}
            </div>

            <button
              type="button"
              onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              style={{
                height: '32px',
                padding: '0 10px',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                background: colors.surface,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>

          <div
            style={{
              background: colors.surface,
              borderRadius: '16px',
              boxShadow: TABLE_SHADOW,
              overflow: 'hidden',
            }}
          >
            {holidayError ? (
              <div className="font-body" style={{ padding: '12px 16px', background: colors.bgSecondary, color: '#991B1B', borderBottom: '1px solid #FCA5A5' }}>
                {holidayError}
              </div>
            ) : null}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                background: colors.bgSecondary,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <div
                  key={day}
                  className="font-body"
                  style={{ padding: '10px 12px', fontSize: '12px', fontWeight: 600, color: colors.textMuted }}
                >
                  {day}
                </div>
              ))}
            </div>

            {calendarError && !calendarLoading ? (
              <LoadErrorState message={calendarError} onRetry={() => void loadCalendar()} margin="24px" />
            ) : calendarLoading ? (
              <div className="font-body" style={{ padding: '20px', color: colors.textMuted }}>
                Loading calendar...
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                {calendarDays.map((day) => {
                  const today = new Date()
                  today.setHours(0, 0, 0, 0)
                  const dayMidnight = new Date(day.date)
                  dayMidnight.setHours(0, 0, 0, 0)
                  const dateKey = toDateKey(day.date)
                  const entry = calendarData[dateKey]
                  const holiday = holidayMap[dateKey]
                  const {
                    orders: ordersPlaced,
                    revenue: orderRevenue,
                    cancelled: cancelledOrders,
                  } = calendarDaySummary(entry)
                  const hasOrders = ordersPlaced > 0
                  const isToday = dayMidnight.getTime() === today.getTime()
                  const isPast = dayMidnight.getTime() < today.getTime()
                  const holidayBackground = holiday ? '#FDE2E2' : null

                  return (
                    <button
                      key={dateKey}
                      type="button"
                      onClick={() => {
                        if (day.inMonth) {
                          setSelectedCalendarDate(dateKey)
                        }
                      }}
                      style={{
                        minHeight: '110px',
                        border: `1px solid ${colors.border}`,
                        background: holidayBackground || (hasOrders ? colors.bgSecondary : colors.surface),
                        padding: '10px',
                        textAlign: 'left',
                        cursor: day.inMonth ? 'pointer' : 'default',
                        opacity: day.inMonth ? 1 : 0.5,
                        outline: 'none',
                        boxShadow: isToday ? 'inset 0 0 0 2px #D6B97B' : 'none',
                      }}
                    >
                      <div className="font-body" style={{ fontSize: '12px', fontWeight: 600, color: isPast ? colors.textMuted : colors.textPrimary }}>
                        {day.date.getDate()}
                      </div>
                      {holiday ? (
                        <div className="font-body" style={{ marginTop: '6px', fontSize: '11px', color: '#B91C1C', fontWeight: 600 }}>
                          {holiday.name}
                        </div>
                      ) : null}
                      {entry ? (
                        <div style={{ marginTop: '8px', display: 'grid', gap: '4px' }}>
                          {/* PRIMARY: business received on this day. Keyed on when
                              the order was placed, so a bag collected or paid for
                              later never moves this money to another cell. */}
                          {ordersPlaced > 0 ? (
                            <>
                              <span className="font-body" style={{ fontSize: '11px', color: colors.textMuted }}>
                                Orders <strong style={{ color: colors.textPrimary, fontWeight: 700 }}>{ordersPlaced}</strong>
                              </span>
                              <span className="font-body" style={{ fontSize: '13px', fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}>
                                {formatAmount(orderRevenue)}
                              </span>
                            </>
                          ) : (
                            <span className="font-body" style={{ fontSize: '11px', color: colors.textMuted }}>
                              No orders placed
                            </span>
                          )}
                          {/* Placed here but cancelled. Stated explicitly so the
                              figure above reads as net of them, not as a silent
                              shortfall. */}
                          {cancelledOrders > 0 ? (
                            <span className="font-body" style={{ fontSize: '10px', color: '#B45309' }}>
                              {cancelledOrders} cancelled
                            </span>
                          ) : null}
                          {/* SECONDARY: operational workload. A different
                              population from the sales figures above and never
                              a substitute for them. */}
                          {entry.pickups > 0 || entry.deliveries > 0 ? (
                            <span
                              className="font-body"
                              style={{
                                fontSize: '10px',
                                color: colors.textMuted,
                                borderTop: `1px solid ${colors.border}`,
                                paddingTop: '4px',
                                marginTop: '2px',
                              }}
                            >
                              {[
                                entry.pickups > 0 ? `${entry.pickups} pickup${entry.pickups === 1 ? '' : 's'}` : null,
                                entry.deliveries > 0 ? `${entry.deliveries} delivery${entry.deliveries === 1 ? '' : 'ies'}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <div className="font-body" style={{ marginTop: '8px', fontSize: '11px', color: colors.textMuted }}>
                          No orders
                        </div>
                      )}

                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {selectedCalendarDate ? (
            <div
              style={{
                marginTop: '16px',
                background: colors.surface,
                borderRadius: '16px',
                boxShadow: TABLE_SHADOW,
                padding: '16px 20px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  {/* "Placed", not "on": these are the orders BOOKED that day,
                      the same population the cell counted. */}
                  <h3 className="font-display" style={{ margin: 0, fontSize: '18px', color: colors.textPrimary }}>
                    Orders placed on {selectedCalendarDate}
                  </h3>
                  <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>
                    {selectedCalendarPlaced} order{selectedCalendarPlaced === 1 ? '' : 's'} ·{' '}
                    {formatAmount(selectedCalendarRevenue)}
                    {selectedCalendarCancelled > 0
                      ? ` · ${selectedCalendarCancelled} cancelled (excluded from the total)`
                      : ''}
                  </p>
                </div>
                {/* Operational context for the same day, labelled so it can
                    never be mistaken for the sales figures beside it. */}
                {(selectedCalendarEntry?.pickups || 0) > 0 || (selectedCalendarEntry?.deliveries || 0) > 0 ? (
                  <span className="font-body" style={{ fontSize: '11px', color: colors.textMuted, textAlign: 'right' }}>
                    Scheduled that day: {selectedCalendarEntry?.pickups || 0} pickups ·{' '}
                    {selectedCalendarEntry?.deliveries || 0} deliveries
                  </span>
                ) : null}
              </div>
              {selectedCalendarOrders.length === 0 ? (
                <p className="font-body" style={{ margin: 0, color: colors.textMuted }}>
                  No orders were placed on this date.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {selectedCalendarOrders.map((orderItem) => (
                    <div key={orderItem.id} style={{ border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                          {orderItem.order_number || orderItem.id}
                        </div>
                        <div className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                          {orderItem.customer_name || 'Customer'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="font-body" style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>
                          {formatAmount(Number(orderItem.total || 0))}
                        </div>
                        <div className="font-body" style={{ fontSize: '11px', color: orderItem.cancelled ? '#B45309' : colors.textMuted }}>
                          {orderItem.status ? toLabel(orderItem.status) : '-'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </section>
      ) : null}

      {viewMode === 'map' ? (
        <section style={{ margin: '16px 32px' }}>
          {mapError ? (
            <LoadErrorState message={mapError} onRetry={() => void loadMapOrders()} margin="24px" />
          ) : mapLoading ? (
            <div className="font-body" style={{ padding: '20px', color: colors.textMuted }}>
              Loading map data...
            </div>
          ) : (
            <OrdersMapView
              orders={mapOrders}
              onAssignAgent={(orderId) => {
                setAssignDropdown({
                  orderId,
                  anchorEl: panelAssignTriggerRef.current || document.body,
                })
              }}
              onViewOrder={(orderId) => {
                void handleViewOrder(orderId)
              }}
            />
          )}
        </section>
      ) : null}

      {viewMode === 'list' ? (
        <OrdersTable>
          <div className="responsive-table-wrapper" style={{ overflowX: 'auto' }}>
            {/* minWidth keeps every column (incl. the right-most Action column)
                at a readable size; the wrapper scrolls horizontally on narrower
                screens (e.g. Windows laptops) so Action is never clipped. */}
            <table className="orders-table responsive-table" style={{ width: '100%', minWidth: '1120px', borderCollapse: 'collapse' }}>
            <thead style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
              <tr>
                <th
                  className="font-body"
                  style={{
                    padding: '0 12px',
                    height: '44px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    aria-label="Select all orders"
                    checked={orders.length > 0 && selectedOrders.size === orders.length}
                    onChange={(event) => handleSelectAll(event.currentTarget.checked)}
                  />
                </th>
                {['#', 'Display ID', 'Customer', 'Address', 'Slot', 'Total', 'Status', 'Agent', 'Action'].map(
                  (title, index) => {
                    const className = title === 'Address' ? 'col-address' : title === 'Agent' ? 'col-agent' : undefined

                    return (
                      <th
                        key={title}
                        className={`font-body ${className ?? ''}`}
                        style={{
                          padding: index === 0 ? '0 20px' : '0 12px',
                          height: '44px',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: colors.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {title}
                      </th>
                    )
                  },
                )}
              </tr>
            </thead>

            <tbody>
              {error && !loading ? (
                  <tr>
                    <td colSpan={12}>
                      <ErrorState message={error} onRetry={() => void loadOrders()} />
                    </td>
                  </tr>
                ) : loading ? (
                <tr>
                  <td colSpan={12} className="font-body" style={{ padding: '28px 20px', fontSize: '14px', color: colors.textMuted }}>
                    Loading orders...
                  </td>
                </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={12}>
                      <EmptyState title="No orders found" description="Try adjusting your filters." />
                    </td>
                  </tr>
              ) : (
                orders.map((order, index) => {
                  const isAssigned = Boolean(order.agent_name)
                  const isSelected = selectedOrders.has(order.id)

                  return (
                    <tr
                      key={order.id}
                      // Clicking anywhere on the row opens the detail panel - the
                      // action staff perform dozens of times a day should not
                      // require hitting a small button. Every nested control
                      // stops propagation so the icons still do their own thing.
                      role="button"
                      tabIndex={0}
                      aria-label={`Open order ${order.order_number}`}
                      onClick={() => openOrderPanel(order)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          openOrderPanel(order)
                        }
                      }}
                      style={{
                        height: '60px',
                        cursor: 'pointer',
                        borderBottom: `0.5px solid ${colors.surface}`,
                        borderLeft: isAssigned ? '3px solid transparent' : '3px solid #D6B97B',
                      }}
                      onMouseEnter={(event) => {
                        event.currentTarget.style.background = isAssigned ? colors.bgSecondary : colors.surface
                        if (isAssigned) {
                          event.currentTarget.style.borderLeftColor = '#D6B97B'
                        }
                      }}
                      onMouseLeave={(event) => {
                        event.currentTarget.style.background = 'transparent'
                        event.currentTarget.style.borderLeftColor = isAssigned ? 'transparent' : '#D6B97B'
                      }}
                    >
                      <td style={{ padding: '0 12px', width: '32px' }}>
                        <input
                          type="checkbox"
                          aria-label={`Select order ${order.order_number}`}
                          checked={isSelected}
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => handleToggleSelect(order.id, event.currentTarget.checked)}
                        />
                      </td>
                      <td className="font-body" style={{ padding: '0 20px', fontSize: '13px', fontWeight: 400, color: colors.textMuted }}>
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td style={{ padding: '0 12px', width: '120px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <OrderQRCode
                              qrPayload={order.qr_payload}
                              qrToken={order.qr_token}
                              orderId={order.id}
                              orderNumber={order.order_number}
                              size={32}
                            />
                            <span
                              style={{
                                fontFamily: 'Courier New, monospace',
                                fontSize: '13px',
                                fontWeight: 600,
                                color: colors.textPrimary,
                              }}
                            >
                              {order.order_number}
                            </span>
                          </div>
                          {order.delivery_model === 'express' ? (
                            <span
                              className="font-body"
                              title={[
                                order.delivery_promise_label || 'Approx. delivery: 40 minutes',
                                order.express_slot_key ? `Slot ${order.express_slot_key}` : null,
                                order.workload_minutes != null ? `${order.workload_minutes} min workload` : null,
                              ].filter(Boolean).join(' · ')}
                              style={{
                                alignSelf: 'flex-start',
                                borderRadius: '999px',
                                background: '#FEF2F2',
                                color: '#B91C1C',
                                border: '1px solid #FCA5A5',
                                padding: '2px 7px',
                                fontSize: '10px',
                                fontWeight: 700,
                                lineHeight: 1.2,
                              }}
                            >
                              Express
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td style={{ padding: '0 12px', width: '160px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span
                            className="font-body"
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '999px',
                              background: '#D6B97B',
                              color: colors.textPrimary,
                              display: 'grid',
                              placeItems: 'center',
                              fontSize: '10px',
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(order.customer_name)}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span className="font-body" style={{ fontSize: '14px', fontWeight: 400, color: colors.textPrimary }}>
                              {order.customer_name ? order.customer_name.split(' ')[0] : '-'}
                            </span>
                            {order.customer_phone ? (
                              <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                                {maskPhone(order.customer_phone)}
                              </span>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            aria-label="Send WhatsApp"
                            onClick={(event) => {
                              event.stopPropagation()
                              void handleQuickWhatsApp(order)
                            }}
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '8px',
                              border: 'none',
                              background: '#25D366',
                              color: colors.bgCard,
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'grid',
                              placeItems: 'center',
                              marginLeft: 'auto',
                            }}
                            onMouseEnter={(event) => {
                              event.currentTarget.style.background = '#1EAD54'
                            }}
                            onMouseLeave={(event) => {
                              event.currentTarget.style.background = '#25D366'
                            }}
                          >
                            <MessageCircle size={12} />
                          </button>
                        </div>
                      </td>
                      <td className="col-address" style={{ padding: '0 12px', width: '180px', maxWidth: '180px' }}>
                        <span
                          className="font-body"
                          style={{
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '13px',
                            color: colors.textPrimary,
                          }}
                          title={order.address_summary || '-'}
                        >
                          {order.address_summary || '-'}
                        </span>
                      </td>
                      <td className="font-body" style={{ padding: '0 12px', width: '120px', fontSize: '13px', fontWeight: 400, color: colors.textPrimary }}>
                        {order.pickup_slot || '-'}
                      </td>
                      <td className="font-body" style={{ padding: '0 12px', width: '96px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                        {(() => {
                          // The FINAL amount - includes any manual adjustment the
                          // admin made in the detail panel. Same number the
                          // summary row, the dashboard and the invoice use.
                          const finalValue = orderValueOf(order)
                          const calculated = order.calculated_amount != null ? Number(order.calculated_amount) : null
                          const adjusted = calculated != null && Math.round(calculated * 100) !== Math.round(finalValue * 100)
                          return (
                            <>
                              {formatAmount(finalValue)}
                              {adjusted ? (
                                <div
                                  className="font-body"
                                  title={`Adjusted by admin. Calculated ${formatExactAmount(calculated as number)}`}
                                  style={{ fontSize: '11px', fontWeight: 500, color: colors.textMuted, marginTop: '2px', whiteSpace: 'nowrap' }}
                                >
                                  adj. · was {formatAmount(calculated as number)}
                                </div>
                              ) : Number(order.discount || 0) > 0 ? (
                                <div
                                  className="font-body"
                                  title={order.coupon_code ? `Coupon ${order.coupon_code}: −${formatAmount(Number(order.discount || 0))}` : undefined}
                                  style={{ fontSize: '11px', fontWeight: 500, color: 'var(--color-success, #2e7d32)', marginTop: '2px', whiteSpace: 'nowrap' }}
                                >
                                  −{formatAmount(Number(order.discount || 0))}{order.coupon_code ? ` ${order.coupon_code}` : ''}
                                </div>
                              ) : null}
                            </>
                          )
                        })()}
                      </td>
                      <td style={{ padding: '0 12px', width: '120px' }}>
                        <span
                          role="status"
                          aria-label={toLabel(order.status)}
                          className="font-body"
                          style={{
                            ...getStatusStyles(order.status),
                            borderRadius: '999px',
                            padding: '4px 10px',
                            fontSize: '11px',
                            fontWeight: 500,
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {toLabel(order.status)}
                        </span>
                        {order.group_count && order.group_count > 1 ? (
                          <div title="Multi-service order" style={{ marginTop: 4 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                              {(order.groups || []).map((g, i) => {
                                const done = g.status === 'ready'
                                const bad = ['failed', 'cancelled', 'returned'].includes(g.status)
                                return (
                                  <span
                                    key={`${order.id}-g${i}`}
                                    title={`${g.label} · ${g.status.replace(/_/g, ' ')}`}
                                    style={{
                                      fontSize: '9px', fontWeight: 700, borderRadius: 4, padding: '1px 4px',
                                      background: done ? '#EAF8EE' : bad ? '#FEF2F2' : '#EAF1FA',
                                      color: done ? '#15803D' : bad ? '#B91C1C' : '#1D4ED8',
                                    }}
                                  >
                                    {g.code || g.label.slice(0, 2).toUpperCase()}
                                  </span>
                                )
                              })}
                            </div>
                            <div style={{ marginTop: 2, fontSize: '10px', color: '#7C6F4F', whiteSpace: 'nowrap' }}>
                              {order.items_ready ?? 0}/{order.items_total ?? 0} ready
                            </div>
                          </div>
                        ) : null}
                      </td>
                      <td className="col-agent" style={{ padding: '0 12px', width: '140px' }}>
                        {order.agent_name ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span
                              aria-label={order.agent_is_online ? 'Agent online' : 'Agent offline'}
                              title={order.agent_is_online ? 'Online' : 'Offline'}
                              style={{
                                width: '7px',
                                height: '7px',
                                borderRadius: '999px',
                                background: order.agent_is_online ? '#15803D' : '#9CA3AF',
                                flexShrink: 0,
                              }}
                            />
                            <span
                              className="font-body"
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '999px',
                                background: colors.textPrimary,
                                color: colors.surface,
                                display: 'grid',
                                placeItems: 'center',
                                fontSize: '11px',
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(order.agent_name)}
                            </span>
                            <span className="font-body" style={{ fontSize: '13px', fontWeight: 400, color: colors.textPrimary }}>
                              {order.agent_name}
                            </span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="font-body"
                            onClick={(event) => {
                              event.stopPropagation()
                              setAssignDropdown({
                                orderId: order.id,
                                anchorEl: event.currentTarget,
                              })
                            }}
                            style={{
                              border: '1px solid var(--accent-text)',
                              borderRadius: '999px',
                              background: 'rgba(214,185,123,0.22)',
                              color: 'var(--accent-text)',
                              cursor: 'pointer',
                              padding: '4px 10px',
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            Unassigned
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '0 12px', width: '100px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            type="button"
                            aria-label="Open invoice"
                            onClick={(event) => { event.stopPropagation(); handleOpenInvoice(order.id) }}
                            style={{
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              background: 'transparent',
                              padding: '4px 6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(event) => {
                              event.currentTarget.style.background = colors.surface
                            }}
                            onMouseLeave={(event) => {
                              event.currentTarget.style.background = 'transparent'
                            }}
                          >
                            📄
                          </button>
                          <button
                            type="button"
                            aria-label="Reorder"
                            onClick={(event) => { event.stopPropagation(); handleOpenReorder(order.id) }}
                            style={{
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              background: 'transparent',
                              padding: '4px 6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(event) => {
                              event.currentTarget.style.background = colors.surface
                            }}
                            onMouseLeave={(event) => {
                              event.currentTarget.style.background = 'transparent'
                            }}
                          >
                            🔄
                          </button>
                          <button
                            type="button"
                            className="font-body"
                            onClick={(event) => {
                              event.stopPropagation()
                              openOrderPanel(order)
                            }}
                            style={{
                              border: `1px solid ${colors.border}`,
                              borderRadius: '6px',
                              background: 'transparent',
                              padding: '4px 10px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: colors.textPrimary,
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(event) => {
                              event.currentTarget.style.background = colors.surface
                            }}
                            onMouseLeave={(event) => {
                              event.currentTarget.style.background = 'transparent'
                            }}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            aria-label="More actions"
                            onClick={(event) => event.stopPropagation()}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              padding: '4px',
                            }}
                          >
                            <MoreVertical size={16} color={colors.textMuted} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
          </div>

          <div
            style={{
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 400, color: colors.textMuted }}>
              Showing {startCount}-{endCount} of {totalOrders} orders
            </p>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                disabled={!canGoPrev}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                style={{
                  height: '32px',
                  padding: '0 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  background: colors.bgCard,
                  cursor: canGoPrev ? 'pointer' : 'not-allowed',
                  opacity: canGoPrev ? 1 : 0.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <ChevronLeft size={14} />
                Prev
              </button>
              <button
                type="button"
                disabled={!canGoNext}
                onClick={() => setPage((prev) => prev + 1)}
                style={{
                  height: '32px',
                  padding: '0 10px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  background: colors.bgCard,
                  cursor: canGoNext ? 'pointer' : 'not-allowed',
                  opacity: canGoNext ? 1 : 0.5,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </OrdersTable>
      ) : null}

      <OrderAssignmentModal
        assignDropdown={assignDropdown}
        onClose={() => setAssignDropdown(null)}
        onAssign={(orderId, agentId) => {
          void handleAssignAgent(orderId, agentId)
        }}
      />

      {selectedOrder ? (
        <>
          <OrderDetailPanel
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onAssignAgent={(orderId, anchorEl) => {
              const trigger = anchorEl || panelAssignTriggerRef.current
              if (trigger) {
                setAssignDropdown({
                  orderId,
                  anchorEl: trigger,
                })
              }
            }}
            onReorderSuccess={() => {
              void loadOrders()
              void loadSummary()
            }}
            // A payment/amount edit inside the panel is the one action that moves
            // the table's Total column AND every summary total, so refresh both
            // from the backend rather than patching numbers locally.
            onOrderChanged={(updated) => {
              setOrders((prev) =>
                prev.map((row) =>
                  row.id === updated.id
                    ? {
                        ...row,
                        status: (updated.status as string) ?? row.status,
                        payment_status: updated.payment_status ?? row.payment_status,
                        payment_mode: updated.payment_mode ?? row.payment_mode,
                        final_order_amount:
                          updated.final_amount_paid != null
                            ? Number(updated.final_amount_paid)
                            : updated.final_order_amount != null
                              ? Number(updated.final_order_amount)
                              : row.final_order_amount,
                        calculated_amount:
                          updated.calculated_payable_amount != null
                            ? Number(updated.calculated_payable_amount)
                            : row.calculated_amount,
                        manual_adjustment_amount:
                          updated.manual_adjustment_amount != null
                            ? Number(updated.manual_adjustment_amount)
                            : row.manual_adjustment_amount,
                      }
                    : row,
                ),
              )
              void loadSummary()
            }}
          />
          <OrderTimeline events={Array.isArray((selectedOrder as any)?.timeline) ? (selectedOrder as any).timeline : []} />
        </>
      ) : null}

      <ReorderModal
        isOpen={showReorderModal}
        orderId={reorderTargetId}
        onClose={() => {
          setShowReorderModal(false)
          setReorderTargetId(null)
        }}
        onSuccess={() => {
          void loadOrders()
        }}
      />

      <CreateOrderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          void loadOrders()
        }}
      />

      {showWhatsappModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 90,
            padding: '24px',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '640px',
              background: colors.bgCard,
              borderRadius: '16px',
              border: `1px solid ${colors.border}`,
              boxShadow: '0px 18px 40px rgba(15,46,42,0.2)',
              padding: '20px 22px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <h3 className="font-display" style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                  WhatsApp Links
                </h3>
                <p className="font-body" style={{ margin: '6px 0 0', fontSize: '13px', color: colors.textMuted }}>
                  Send order updates to customers
                </p>
              </div>
              <button
                type="button"
                className="font-body"
                onClick={() => {
                  setShowWhatsappModal(false)
                  setWhatsappLinks([])
                }}
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: '999px',
                  background: 'transparent',
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>

            <div style={{ marginTop: '16px', maxHeight: '360px', overflowY: 'auto' }}>
              {whatsappLinks.length === 0 ? (
                <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>
                  No WhatsApp links available.
                </p>
              ) : (
                whatsappLinks.map((entry) => (
                  <div
                    key={entry.order_number}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '10px 0',
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    <span className="font-body" style={{ fontSize: '13px', color: colors.textPrimary }}>
                      Order #{entry.order_number}
                    </span>
                    <a
                      className="font-body"
                      href={entry.link}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: colors.accent,
                        textDecoration: 'none',
                      }}
                    >
                      Open WhatsApp
                    </a>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}

      {selectedOrders.size > 0 ? (
        <div
          style={{
            position: 'fixed',
            left: '260px',
            right: '24px',
            bottom: '18px',
            background: colors.textPrimary,
            color: colors.surface,
            borderRadius: '16px',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0px 12px 24px rgba(15,46,42,0.24)',
            zIndex: 50,
          }}
        >
          <span className="font-body" style={{ fontSize: '13px', fontWeight: 600 }}>
            {selectedOrders.size} orders selected
          </span>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={bulkStatus}
              onChange={(event) => setBulkStatus(event.target.value)}
              className="font-body"
              style={{
                height: '34px',
                borderRadius: '8px',
                border: '1px solid rgba(214,185,123,0.4)',
                background: colors.textPrimary,
                color: colors.surface,
                padding: '0 10px',
                fontSize: '12px',
              }}
            >
              <option value="">Change Status</option>
              {['placed', 'agent_assigned', 'picked_up', 'at_facility', 'in_wash', 'in_iron', 'quality_check', 'ready', 'out_for_delivery', 'delivered', 'cancelled'].map(
                (status) => (
                  <option key={status} value={status}>
                    {toLabel(status)}
                  </option>
                ),
              )}
            </select>
            <button
              type="button"
              className="font-body"
              disabled={bulkBusy}
              onClick={() => {
                void handleBulkStatusUpdate()
              }}
              style={{
                height: '34px',
                padding: '0 12px',
                borderRadius: '8px',
                border: 'none',
                background: '#D6B97B',
                color: colors.textPrimary,
                fontSize: '12px',
                fontWeight: 600,
                cursor: bulkBusy ? 'not-allowed' : 'pointer',
                opacity: bulkBusy ? 0.6 : 1,
              }}
            >
              {bulkBusy ? 'Applying…' : 'Apply'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <select
              value={bulkAgentId}
              onChange={(event) => setBulkAgentId(event.target.value)}
              className="font-body"
              style={{
                height: '34px',
                borderRadius: '8px',
                border: '1px solid rgba(214,185,123,0.4)',
                background: colors.textPrimary,
                color: colors.surface,
                padding: '0 10px',
                fontSize: '12px',
              }}
            >
              <option value="">Assign Agent</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name || agent.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="font-body"
              disabled={bulkBusy}
              onClick={() => {
                void handleBulkAssignAgent()
              }}
              style={{
                height: '34px',
                padding: '0 12px',
                borderRadius: '8px',
                border: 'none',
                background: '#D6B97B',
                color: colors.textPrimary,
                fontSize: '12px',
                fontWeight: 600,
                cursor: bulkBusy ? 'not-allowed' : 'pointer',
                opacity: bulkBusy ? 0.6 : 1,
              }}
            >
              {bulkBusy ? 'Assigning…' : 'Assign'}
            </button>
          </div>

          <button
            type="button"
            className="font-body"
            onClick={() => setSelectedOrders(new Set())}
            style={{
              height: '34px',
              padding: '0 12px',
              borderRadius: '8px',
              border: '1px solid rgba(214,185,123,0.6)',
              background: 'transparent',
              color: '#D6B97B',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Deselect All
          </button>

          <button
            type="button"
            className="font-body"
            onClick={() => {
              void handleBulkPrintLabels()
            }}
            style={{
              height: '34px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#D6B97B',
              color: colors.textPrimary,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Print Labels
          </button>
        </div>
      ) : null}
    </div>
  )
}
